/* eslint-disable @typescript-eslint/member-ordering */
import { logger } from '@accedo/logger';
import { Route } from '##/dataModels/route';
import { PageLayout, PageTemplates } from '##/dataModels/pageLayout';
import { CmsService } from '##/services/elevate/types';
import { MenuItem } from '##/dataModels/menuItem';
import { apiCall } from '##/services/nzos/adapter';

// last known good constants
const PUBLIC_PATH = process.env.PUBLIC_PATH || '/';
const LKG_ROUTES = `LKG_RTS_${PUBLIC_PATH}`;
const LKG_PAGE_LAYOUT_PREFIX = `LKG_PL_${PUBLIC_PATH}_`;
const LKG_MENU = `LKG_MU_${PUBLIC_PATH}`;
const CMS_ROUTE_EXPIRY = 1000 * 60 * 1; // 1 minute cache to allow for service startup
const CMS_ROUTE_KEY_EXPIRY = `CMS_R_E_${PUBLIC_PATH}`;
const CMS_MENU_EXPIRY = 1000 * 60 * 1; // 1 minute cache to allow for service startup
const CMS_MENU_KEY_EXPIRY = `CMS_M_E_${PUBLIC_PATH}`;

// logger prefix
const LP = '[NzosCMSService]';

const cleanRoutePath = (route: string): string => {
  const noWildcard = route.replace(/\*$/, '');
  const noEndSlash = noWildcard.endsWith('/') && noWildcard.length > 1 ? noWildcard.slice(0, -1) : noWildcard;
  return noEndSlash;
};

/**
 * Helper to extract route ID from malformed menu route URLs.
 * Backend currently returns full API URLs like:
 * "https://api.nzonscreen.uat.octave.nz/v1/cms/page-layout/route-1"
 * We need to extract "route-1" to fetch the page layout.
 */
const extractRouteIdFromUrl = (routeUrl: string | null): string | null => {
  if (!routeUrl) return null;

  // If it's already a simple route ID or path, return as-is
  if (!routeUrl.includes('/cms/page-layout/')) {
    return routeUrl;
  }

  // Extract the route ID from the URL
  const match = routeUrl.match(/\/cms\/page-layout\/([^/?]+)/);
  return match ? match[1] : null;
};

/**
 * Map menu item IDs to route paths.
 * This defines the actual application routes for each menu item.
 */
const MENU_ID_TO_ROUTE: Record<string, string> = {
  'home': '/',
  'movies': '/movies',
  'short-films': '/short-films',
  'tv-shows': '/tv-shows',
  'my-list': '/my-list',
  'search': '/search',
  'settings': '/settings',
};

/**
 * NZOS CMS Service.
 *
 * Provides CMS functionality using NZOS middleware API endpoints:
 * - GET /cms/page-layout/{routeId} - Get page layout for a specific route.
 * - GET /cms/menu - Get application menu structure.
 *
 * Routes are derived from menu items since the API doesn't have a dedicated routes endpoint.
 * Transforms NZOS API responses to match app's data models.
 */
export class NzosCMSService implements CmsService {
  // eslint-disable-next-line no-use-before-define
  private static instance?: NzosCMSService;
  // eslint-disable-next-line @typescript-eslint/no-empty-function, no-empty-function
  private constructor() {}

  static getInstance(): NzosCMSService {
    NzosCMSService.instance ??= new NzosCMSService();
    return NzosCMSService.instance;
  }

  /**
   * Transform menu item to app's Route format.
   * Creates a lightweight route object with a minimal page reference.
   * The full page layout is fetched later by getPageLayout() when needed.
   * @param menuItem - Menu item object.
   * @param originalRoute - Original API URL before transformation (needed to extract route ID).
   */
  private static transformMenuItemToRoute(menuItem: MenuItem, originalRoute: string | null): Route | null {
    // Get the actual app route path from menu ID
    const routePath = MENU_ID_TO_ROUTE[menuItem.id];

    if (!routePath) {
      logger.warn(`${LP} Menu item "${menuItem.id}" not in MENU_ID_TO_ROUTE mapping, skipping`);
      return null;
    }

    // Extract route ID from the ORIGINAL route field (before transformation)
    let routeId = null;

    if (originalRoute) {
      routeId = extractRouteIdFromUrl(originalRoute);
    }

    if (!routeId) {
      logger.warn(`${LP} Menu item "${menuItem.id}" has no valid route, skipping`);
      return null;
    }

    // Create lightweight route with minimal page reference
    // The page layout will be fetched by getPageLayout() when this route is navigated to
    return {
      id: menuItem.id,
      route: cleanRoutePath(routePath),
      displayText: menuItem.label,
      page: {
        id: routeId, // Store the route ID so getPageLayout can fetch the full layout
        template: PageTemplates.modular, // Placeholder - will be replaced when page layout is fetched
        containerIds: [],
      },
    };
  }

  /**
   * Fetch page layout by route ID (e.g., "route-1", "route-2").
   */
  // eslint-disable-next-line class-methods-use-this
  private async fetchPageLayoutByRouteId(routeId: string, segment?: string): Promise<PageLayout> {
    return apiCall<PageLayout>(`/cms/page-layout/${routeId}`, {
      params: segment ? { segment } : undefined,
    });
  }

  /**
   * Get all application routes from NZOS CMS.
   */
  async getRoutes(segment?: string): Promise<Route[]> {
    try {
      const routeExpiry: string | null = sessionStorage.getItem(CMS_ROUTE_KEY_EXPIRY);
      const currentTime = Date.now();
      if (routeExpiry && currentTime < Number(routeExpiry)) {
        const cachedRoutes = localStorage.getItem(LKG_ROUTES);
        if (cachedRoutes) {
          return JSON.parse(cachedRoutes);
        }
      }

      const segmentInfo = segment ? ` for segment: ${segment}` : '';
      logger.debug(`${LP} Deriving routes from menu${segmentInfo}`);

      // Fetch ORIGINAL menu items (with API URLs) for route creation
      // Use getMenu() which handles caching properly
      const originalMenuItems = await this.getMenu(segment);
      logger.debug(`${LP} Got ${originalMenuItems.length} menu items`);

      // BUT we need the ORIGINAL API URLs, not the transformed ones
      // So fetch directly from API if menu was already transformed
      let menuItemsWithOriginalRoutes = originalMenuItems;

      // Check if menu items have already been transformed (route is app path, not API URL)
      if (
        originalMenuItems.length > 0 &&
        originalMenuItems[0].route &&
        !originalMenuItems[0].route.includes('/cms/page-layout/')
      ) {
        menuItemsWithOriginalRoutes = await apiCall<MenuItem[]>('/cms/menu', {
          params: segment ? { segment } : undefined,
        });
      }

      // Transform menu items to routes using ORIGINAL API URLs
      const routesOrNull = menuItemsWithOriginalRoutes.map((menuItem) =>
        NzosCMSService.transformMenuItemToRoute(menuItem, menuItem.route),
      );

      // Filter out null values (menu items without valid routes)
      const routes = routesOrNull.filter((route): route is Route => route !== null);
      logger.info(`${LP} Created ${routes.length} routes from menu items`);

      if (routes.length === 0) {
        logger.error(`${LP} No valid routes found in menu!`);
        throw new Error('No valid routes found in menu');
      }

      // Sort by length (longest first for better matching)
      routes.sort((routeA, routeB) => routeB.route.length - routeA.route.length);

      localStorage.setItem(LKG_ROUTES, JSON.stringify(routes));
      sessionStorage.setItem(CMS_ROUTE_KEY_EXPIRY, String(currentTime + CMS_ROUTE_EXPIRY));

      return routes;
    } catch (exception) {
      const cachedRoutes = localStorage.getItem(LKG_ROUTES);
      if (cachedRoutes) {
        logger.error(`${LP} Error getting routes, returning cached response, `, exception);
        return JSON.parse(cachedRoutes);
      }
      throw new Error(`${LP} Error getting routes, no cached response, ${exception}`);
    }
  }

  /**
   * Get page layout for a specific route from NZOS CMS.
   * This uses the routes loaded by getRoutes() to find the route's page ID,
   * then fetches the full page layout from the API.
   */
  async getPageLayout(routeString: string, segment?: string): Promise<PageLayout> {
    try {
      const comparedRoute = routeString.startsWith('/') ? routeString : `/${routeString}`;

      // Special handling for genre & detail page routes: they use page layout IDs directly
      // Genre routes like "/genre-1" should fetch from "/cms/page-layout/genre-1"
      // Detail routes like "/movie/movie-1", "/short-film/short-film-1", "/show/show-1"
      // should fetch from "/cms/page-layout/{id}" without looking up in routes

      let pageLayoutId: string | null = null;
      let isDetailPage = false;

      // Check for genre routes: /genre-1
      if (comparedRoute.match(/^\/genre-\d+$/)) {
        pageLayoutId = comparedRoute.substring(1); // Remove leading slash
      }
      // Check for detail page routes: /movie/, /short-film/, /show/
      else {
        const detailPagePrefixes = ['/movie/', '/short-film/', '/show/'];
        const matchedPrefix = detailPagePrefixes.find((prefix) => comparedRoute.startsWith(prefix));

        if (matchedPrefix) {
          pageLayoutId = comparedRoute.replace(matchedPrefix, '');
          isDetailPage = true;
        }
      }

      if (pageLayoutId) {
        const segmentInfo = segment ? ` (segment: ${segment})` : '';
        logger.debug(`${LP} Fetching page layout directly for ID: ${pageLayoutId}${segmentInfo}`);

        const pageLayout = await this.fetchPageLayoutByRouteId(pageLayoutId, segment);

        // For detail pages (movie, short-film, show), remove the first container (hero banner is hardcoded in the page)
        if (isDetailPage && pageLayout.containersIds && pageLayout.containersIds.length > 0) {
          logger.debug(`${LP} Removing first container for detail page`);
          // Preserve callToActions from the hero container before stripping it
          if (pageLayout.containers && pageLayout.containers.length > 0) {
            const heroContainer = pageLayout.containers[0];
            const heroCTAs = heroContainer.callToActions ?? heroContainer.items?.[0]?.callToActions;
            if (heroCTAs && heroCTAs.length > 0) {
              pageLayout.heroCallToActions = heroCTAs;
            }
            pageLayout.containers = pageLayout.containers.slice(1);
          }
          pageLayout.containersIds = pageLayout.containersIds.slice(1);
        }
        localStorage.setItem(`${LKG_PAGE_LAYOUT_PREFIX}${routeString}`, JSON.stringify(pageLayout));
        return pageLayout;
      }

      // Standard route handling: look up route to get page ID
      const routes = await this.getRoutes(segment);
      const routeObject = routes.find(
        (rte: Route) => rte.route === comparedRoute || comparedRoute.startsWith(rte.route),
      );

      if (!routeObject) {
        throw new Error(`No route found for: ${routeString}`);
      }

      const segmentInfo = segment ? ` (segment: ${segment})` : '';
      logger.debug(`${LP} Fetching page layout for route: ${comparedRoute}${segmentInfo}`);

      // Fetch the full page layout using the route's page ID
      const pageLayout = await this.fetchPageLayoutByRouteId(routeObject.page.id, segment);

      localStorage.setItem(`${LKG_PAGE_LAYOUT_PREFIX}${routeString}`, JSON.stringify(pageLayout));

      return pageLayout;
    } catch (exception) {
      const cachedPageLayout = localStorage.getItem(`${LKG_PAGE_LAYOUT_PREFIX}${routeString}`);
      if (cachedPageLayout) {
        logger.error(`${LP} Error getting page layout, returning cached response, `, exception);
        return JSON.parse(cachedPageLayout);
      }
      throw new Error(`Cannot get page layout for ${routeString} ${exception}`);
    }
  }

  /**
   * Get default theme (not implemented in NZOS API yet).
   */
  // eslint-disable-next-line class-methods-use-this
  async getDefaultTheme(segment?: string): Promise<string> {
    const segmentInfo = segment ? `for segment ${segment}` : '';
    throw new Error(`getDefaultTheme not implemented ${segmentInfo}`);
  }

  /**
   * Get application menu from NZOS CMS.
   */
  // eslint-disable-next-line class-methods-use-this
  async getMenu(segment?: string): Promise<MenuItem[]> {
    try {
      const menuExpiry: string | null = sessionStorage.getItem(CMS_MENU_KEY_EXPIRY);
      const currentTime = Date.now();
      if (menuExpiry && currentTime < Number(menuExpiry)) {
        logger.debug(`${LP} Using cached menu`);
        const cachedMenu = localStorage.getItem(LKG_MENU);
        if (cachedMenu) {
          return JSON.parse(cachedMenu);
        }
      }

      const segmentInfo = segment ? ` for segment: ${segment}` : '';
      logger.debug(`${LP} Fetching menu from NZOS CMS${segmentInfo}`);

      const menuItems = await apiCall<MenuItem[]>('/cms/menu', {
        params: segment ? { segment } : undefined,
      });

      // Transform menu items to replace API URLs with app route paths
      const transformedMenuItems = menuItems.map((item) => {
        const appRoute = MENU_ID_TO_ROUTE[item.id];
        if (appRoute) {
          return { ...item, route: appRoute };
        }
        return item;
      });

      localStorage.setItem(LKG_MENU, JSON.stringify(transformedMenuItems));
      sessionStorage.setItem(CMS_MENU_KEY_EXPIRY, String(currentTime + CMS_MENU_EXPIRY));

      return transformedMenuItems;
    } catch (exception) {
      const cachedMenu = localStorage.getItem(LKG_MENU);
      if (cachedMenu) {
        logger.error(`${LP} Error getting menu, returning cached response, `, exception);
        return JSON.parse(cachedMenu);
      }
      throw new Error(`${LP} Error getting menu, ${exception}`);
    }
  }
}
